import { defineStore } from 'pinia'
import { Preferences } from '@capacitor/preferences'
import { useLogger } from '../composables/useLogger'

// Constants for preference keys
export const PREFERENCE_KEYS = {
  FIRST_TIME_ON_SIGNIN_SIGNON: 'firstTimeOnSigninSignon',  // first time on the signin signon forms - used to redirect to signup
  FIRST_LAUNCH_ON_DEVICE: 'firstLaunchOnDevice',  // first time on the device - used in posthog for funnels
  SPEECH_BUBBLE_FINISHED_ALBUMS_CREATE: 'speech_bubble_finished_albums_create',  // whether the albums create bubble has been shown
  CALENDAR_WELCOME_APPOINTMENT_SHOWN: 'calendar_welcome_appointment_shown',  // tracks whether the welcome appointment has been shown
  CLOCK_WIZARD_VIEWED: 'clock_wizard_viewed',  // tracks whether the user has seen the clock wizard
  KNOWLEDGE_GRAPH_DATA: 'knowledge_graph_data',  // stores the knowledge graph data as JSON
  SENIOR_VOICE_CHAT_FIRST_TIME: 'senior_voice_chat_first_time',  // tracks whether the user has seen the senior voice chat first-time instructions
} as const

// Type for preference keys
export type PreferenceKey = typeof PREFERENCE_KEYS[keyof typeof PREFERENCE_KEYS]

export const usePreferencesStore = defineStore('preferences', () => {
  const logger = useLogger()

  /**
   * Check if a preference exists and has a value
   */
  async function hasValue(key: PreferenceKey): Promise<boolean> {
    try {
      const { value } = await Preferences.get({ key })
      return value != null && value !== ''
    } catch (error) {
      logger.logErrorCaught('Error checking preference value', 'preferencesStore.ts', error)
      return false
    }
  }

  /**
   * Get a preference value
   * Returns null if the key doesn't exist or if there was an error
   */
  async function getValue(key: PreferenceKey): Promise<string | null> {
    try {
      const { value } = await Preferences.get({ key })
      // For non-existing keys, value will be null
      return value
    } catch (error) {
      logger.logErrorCaught('Error getting preference value', 'preferencesStore.ts', error)
      return null
    }
  }

  /**
   * Set a preference value
   */
  async function setValue(key: PreferenceKey, value: string): Promise<boolean> {
    try {
      await Preferences.set({ key, value })
      return true
    } catch (error) {
      logger.logErrorCaught('Error setting preference value', 'preferencesStore.ts', error)
      return false
    }
  }

  /**
   * Remove a preference
   */
  async function removeValue(key: PreferenceKey): Promise<boolean> {
    try {
      await Preferences.remove({ key })
      return true
    } catch (error) {
      logger.logErrorCaught('Error removing preference value', 'preferencesStore.ts', error)
      return false
    }
  }

  /**
   * Clear all preferences
   */
  async function clearAll(): Promise<boolean> {
    try {
      await Preferences.clear()
      return true
    } catch (error) {
      logger.logErrorCaught('Error clearing preferences', 'preferencesStore.ts', error)
      return false
    }
  }

  /**
   * Check if this is the first launch
   */
  async function isFirstTimeOnSigninSignon(): Promise<boolean> {
    return !(await hasValue(PREFERENCE_KEYS.FIRST_TIME_ON_SIGNIN_SIGNON))
  }

  /**
   * Mark the app as launched
   */
  async function markAsFirstTimeSigninSignon(): Promise<void> {
    await setValue(PREFERENCE_KEYS.FIRST_TIME_ON_SIGNIN_SIGNON, 'true')
  }

  /**
   * Check if this is the first launch on this device
   */
  async function isFirstLaunchOnDevice(): Promise<boolean> {
    return !(await hasValue(PREFERENCE_KEYS.FIRST_LAUNCH_ON_DEVICE))
  }

  /**
   * Mark the app as launched on this device
   */
  async function markAsLaunchedOnDevice(): Promise<void> {
    await setValue(PREFERENCE_KEYS.FIRST_LAUNCH_ON_DEVICE, 'true')
  }

  return {
    hasValue,
    getValue,
    setValue,
    removeValue,
    clearAll,
    isFirstTimeOnSigninSignon,
    markAsFirstTimeSigninSignon,
    isFirstLaunchOnDevice,
    markAsLaunchedOnDevice
  }
}) 